import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Talk } from '../components/Talk';
import { WhoAmI } from '../components/WhoAmI';
import type { Challenge, FieldSpec, Work } from '../data/types';
import { photoForCard } from '../lib/photo';
import { track } from '../analytics';

const EMOJI = /\p{Extended_Pictographic}(?:️|‍\p{Extended_Pictographic})*/gu;

/** The one input per exercise, driven by input.kind in the data. */
export interface RunnerCopy { placeholder?: string; hint?: string }

export function RunnerInput({ challenge, work, onChange, onProgress, copy, subject, onFinish, onPhotoBusy }: { challenge: Challenge; work: Work; onChange: (w: Work) => void; onProgress?: (p: { filled: number; count: number } | null) => void; copy?: RunnerCopy; subject?: string; onFinish?: (w: Work) => void; onPhotoBusy?: (busy: boolean) => void }) {
  const spec = challenge.input;
  const fileRef = useRef<HTMLInputElement>(null);
  // The work as it is now, not as it was when a photo started shrinking.
  const latest = useRef(work);
  useLayoutEffect(() => { latest.current = work; });

  if (spec.kind === 'talk') {
    return (
      <Talk subject={subject ? `…about you and ${subject}.` : '…about you and a friend.'} words={spec.words || []} seconds={spec.seconds || 75} minSeconds={spec.minSeconds || 20} every={spec.every || 18}
        prompt="Just talk. Something that happened to you two, or nearly did."
        onDone={(r) => { const next = { ...work, audio: r.blob, audioSec: r.seconds }; onChange(next); onFinish?.(next); }} />
    );
  }

  if (spec.kind === 'whoami') {
    return <WhoAmI challenge={challenge} work={work} onChange={onChange} onProgress={onProgress} onFinish={onFinish} onPhotoBusy={onPhotoBusy} />;
  }

  if (spec.kind === 'lines' || spec.kind === 'pairs') {
    return <Paced challenge={challenge} work={work} onChange={onChange} onProgress={onProgress} copy={copy} />;
  }

  if (spec.kind === 'letter') {
    const count = spec.count ?? 6;
    const lines = Array.from({ length: count }, (_, i) => work.lines?.[i] ?? '');
    const filled = lines.filter((l) => l.trim()).length;
    const setLine = (i: number, v: string) => { const next = [...lines]; next[i] = v; onChange({ ...work, lines: next }); };
    const setField = (k: string, v: string) => onChange({ ...work, fields: { ...(work.fields || {}), [k]: v } });
    return (
      <div className="stack">
        {spec.kind === 'letter' && (
          <input className="input" value={work.fields?.salutation ?? spec.salutation ?? ''} onChange={(e) => setField('salutation', e.target.value)} aria-label="Salutation" />
        )}
        {lines.map((l, i) => (
          <div className="line" key={i}>
            <span className={`n ${l.trim() ? '' : 'empty'}`}>{i + 1}</span>
            <textarea className="input" rows={2} value={l} onChange={(e) => setLine(i, e.target.value)} placeholder={i === 0 ? 'The first grievance' : ''} />
          </div>
        ))}
        {spec.kind === 'letter' && (
          <textarea className="input" rows={2} value={work.fields?.signoff ?? spec.signoff ?? ''} onChange={(e) => setField('signoff', e.target.value)} aria-label="Sign-off" placeholder={'Yours,\nThe Left Shoe'} />
        )}
        <div className="row between"><span className="counter">{filled} of {count}</span><span className="hint">Ending early is fine.</span></div>
      </div>
    );
  }

  if (spec.kind === 'fields') {
    return (
      <div className="stack">
        <FieldsInput fields={spec.fields || []} work={work} onChange={onChange} />
        <span className="hint">Ending early is fine.</span>
      </div>
    );
  }

  if (spec.kind === 'text') {
    // `private` is Pages and its kin: a page the app has promised nobody reads.
    // The attribute is also what keeps it out of a session replay (analytics.ts).
    return (
      <div className="stack" data-private={spec.private || undefined}>
        <textarea className="input" rows={spec.rows || 6} value={work.text || ''} onChange={(e) => onChange({ ...work, text: e.target.value })} placeholder={spec.label} />
        <div className="row between">
          <span className="counter">{(work.text || '').trim().split(/\s+/).filter(Boolean).length} words</span>
          <span className="hint">{spec.note || 'Ending early is fine.'}</span>
        </div>
      </div>
    );
  }

  if (spec.kind === 'emoji') {
    const raw = work.text || '';
    const emoji = raw.match(EMOJI) || [];
    return (
      <div className="stack">
        <input className="input" value={raw} onChange={(e) => onChange({ ...work, text: e.target.value, emoji: (e.target.value.match(EMOJI) || []).slice(0, 12) })} placeholder="Tap the emoji key" style={{ fontSize: 28, letterSpacing: '.08em' }} inputMode="text" />
        <div className="row between"><span className="counter">{Math.min(emoji.length, 12)} of 12</span><span className="hint">Ending early is fine.</span></div>
        {spec.secret && (
          <div className="field" data-private>
            <label htmlFor="secret">{spec.secret}</label>
            <input id="secret" className="input" value={work.secret || ''} onChange={(e) => onChange({ ...work, secret: e.target.value })} />
          </div>
        )}
      </div>
    );
  }

  // photo: paper plus a photograph. No drawing canvas; drawing is always paper (10 §5).
  const onFile = (file: File | undefined) => {
    if (!file) return;
    // Shrunk before it is held, not before it is sent: a raw camera JPEG is what
    // breaks the card render (see lib/photo.ts).
    //
    // Until it lands, Done waits (onPhotoBusy). A novelist in interview 2
    // (2026-09-20, iPhone) photographed her drawing and got a card with no
    // photo in it: Done could be pressed while the picture was still being
    // read, and the card was made from the work without it.
    onPhotoBusy?.(true);
    void photoForCard(file)
      .then((photo) => { onChange({ ...latest.current, photo }); track('photo_added', { challengeId: challenge.id }); })
      .catch((e) => { track('photo_failed', { challengeId: challenge.id, error: String(e).slice(0, 120) }); })
      .finally(() => onPhotoBusy?.(false));
  };
  return (
    <div className="stack">
      <label className="photo-drop">
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={(e) => onFile(e.target.files?.[0])} />
        {work.photo ? <img src={work.photo} alt="Your drawing" /> : (
          <>
            <span style={{ fontSize: 40 }}>📷</span>
            <span className="counter">{spec.label}</span>
            <span className="hint">Draw on paper, then photograph it.</span>
          </>
        )}
      </label>
      {work.photo && <button className="link" onClick={() => fileRef.current?.click()}>Take it again</button>}
    </div>
  );
}

/**
 * Labelled fields written into `work.fields`: a fields exercise's whole input,
 * and the closing step (`input.then`) after any other kind.
 */
export function FieldsInput({ fields, work, onChange }: { fields: FieldSpec[]; work: Work; onChange: (w: Work) => void }) {
  const F = work.fields || {};
  const set = (k: string, v: string) => onChange({ ...work, fields: { ...F, [k]: v } });
  return (
    <>
      {fields.map((f) => (
        <div className="field" key={f.key}>
          <label htmlFor={`f-${f.key}`}>{f.label}</label>
          {f.rows && f.rows > 1
            ? <textarea id={`f-${f.key}`} className="input" rows={f.rows} value={F[f.key] || ''} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} />
            : <input id={`f-${f.key}`} className="input" value={F[f.key] || ''} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} style={f.short ? { maxWidth: 160 } : undefined} />}
          {/* one-tap answers: a question about yourself is easier to start from a list than from nothing */}
          {f.options && (
            <div className="chips">
              {f.options.map((o) => <button type="button" key={o} className={`chip ${F[f.key] === o ? 'on' : ''}`} onClick={() => set(f.key, o)}>{o}</button>)}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

/**
 * One thing at a time (Finch's pace). The list grows above the single input;
 * tapping an earlier entry reopens it. Enter or Next moves on. Nothing here
 * counts down and nothing asks for all ten at once.
 */
function Paced({ challenge, work, onChange, onProgress, copy }: { challenge: Challenge; work: Work; onChange: (w: Work) => void; onProgress?: (p: { filled: number; count: number } | null) => void; copy?: RunnerCopy }) {
  const spec = challenge.input;
  const pairs = spec.kind === 'pairs';
  const count = spec.count ?? (pairs ? 5 : 6);
  const lines = Array.from({ length: count }, (_, i) => work.lines?.[i] ?? '');
  const pairVals = Array.from({ length: count }, (_, i) => work.pairs?.[i] ?? { name: '', sub: '' });
  const has = (i: number) => (pairs ? pairVals[i].name.trim() : lines[i].trim()) !== '';
  const filled = Array.from({ length: count }, (_, i) => has(i)).filter(Boolean).length;
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  /**
   * Whether the one live field is on screen. It comes down once every slot is
   * filled, and goes back up if an earlier entry is tapped to fix it.
   *
   * Before, it never came down: the last entry left the cursor sitting on a
   * full field with the button greyed out reading "All done", so the entry
   * looked like it had not registered and the only way on was a second Done
   * further down the screen. Six-Word Film, which is three slots, hit this on
   * entry three — about a minute into the exercise.
   */
  const [editing, setEditing] = useState(false);

  useEffect(() => { onProgress?.({ filled, count }); }, [filled, count, onProgress]);
  useEffect(() => () => onProgress?.(null), [onProgress]);
  useEffect(() => { inputRef.current?.focus(); }, [cursor, editing]);

  const setLine = (i: number, v: string) => { const next = [...lines]; next[i] = v; onChange({ ...work, lines: next }); };
  const setPair = (i: number, k: 'name' | 'sub', v: string) => { const next = pairVals.map((p) => ({ ...p })); next[i][k] = v; onChange({ ...work, pairs: next }); };
  const advance = () => {
    // to the next empty slot after the cursor, else the first empty one, else stay
    const after = Array.from({ length: count }, (_, i) => i).find((i) => i > cursor && !has(i));
    const any = Array.from({ length: count }, (_, i) => i).find((i) => !has(i));
    setCursor(after ?? any ?? cursor);
  };
  const prefix = (i: number) => (spec.prefixes ? spec.prefixes[i % spec.prefixes.length] : null);
  const noun = spec.placeholder || 'Line';
  const allDone = filled >= count;
  const open = !allDone || editing;
  /** Enter, and the button: on to the next empty slot, or put the field away. */
  const next = () => { if (allDone) setEditing(false); else advance(); };

  return (
    <div className="stack">
      {Array.from({ length: count }, (_, i) => i).filter((i) => has(i) && !(open && i === cursor)).map((i) => (
        <button key={i} className="paced-done" onClick={() => { setCursor(i); setEditing(true); }} type="button">
          <span className="n">{spec.alphabet ? String.fromCharCode(65 + i) : i + 1}</span>
          <span className="paced-text">
            {pairs ? <><b>{pairVals[i].name}</b>{pairVals[i].sub ? <span className="muted"> · {pairVals[i].sub}</span> : null}</> : <>{prefix(i) ? <b className={i % 2 === 0 ? 'good' : 'bad'}>{prefix(i)} </b> : null}{lines[i]}</>}
          </span>
        </button>
      ))}
      {open && (
        <form className="paced-now" onSubmit={(e) => { e.preventDefault(); if (has(cursor)) next(); }}>
          <div className="row between">
            <span className="counter">{spec.alphabet ? String.fromCharCode(65 + cursor) : `${noun} ${cursor + 1}`}{!spec.alphabet && <span className="muted"> of {count}</span>}</span>
            {prefix(cursor) && <span className={`prefix ${cursor % 2 === 0 ? 'good' : 'bad'}`} style={{ marginTop: 0 }}>{prefix(cursor)}</span>}
          </div>
          {pairs ? (
            <div className="stack" style={{ gap: 6 }}>
              <input ref={inputRef} className="input" value={pairVals[cursor].name} placeholder={spec.nameLabel || 'Name'} onChange={(e) => setPair(cursor, 'name', e.target.value)} style={{ fontWeight: 900, letterSpacing: '.02em' }} enterKeyHint="next"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.currentTarget.parentElement?.querySelector('input:nth-of-type(2)') as HTMLInputElement | null)?.focus(); } }} />
              <input className="input" value={pairVals[cursor].sub} placeholder={spec.subLabel || ''} onChange={(e) => setPair(cursor, 'sub', e.target.value)} style={{ fontSize: 15 }} enterKeyHint="next"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (has(cursor)) next(); } }} />
            </div>
          ) : (
            <input ref={inputRef} className="input big" value={lines[cursor]} placeholder={cursor === 0 ? (copy?.placeholder || 'Don’t be afraid of bad ideas.') : ''} onChange={(e) => setLine(cursor, e.target.value)} enterKeyHint="next"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (e.currentTarget.value.trim()) next(); } }} />
          )}
          <div className="row between">
            <span className="dots">{Array.from({ length: count }, (_, i) => <span key={i} className={`dot ${has(i) ? '' : 'off'}`} />)}</span>
            <button type="submit" className="btn small" style={{ background: 'var(--navy)', boxShadow: 'inset 0 -4px 0 #143B7F' }} disabled={!has(cursor)}>{allDone ? 'Finish' : 'Next'}</button>
          </div>
        </form>
      )}
      <span className="hint">{allDone ? 'That’s all.' : (copy?.hint || 'One at a time. Stop whenever you like.')}</span>
    </div>
  );
}
